import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Zap, 
  X,
  Loader2,
  Heart,
  Sparkles,
  Music2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useNWC } from '@/hooks/useNWCContext';
import { useToast } from '@/hooks/useToast';
import { WalletModal } from '@/components/WalletModal';
import { cn } from '@/lib/utils';
import { CoveLogo } from '@/components/CoveLogo';

const SUPPORT_AMOUNTS = [21, 100, 500, 1000, 2100, 5000, 10000, 21000];

// The app's lightning address for receiving support
const APP_LIGHTNING_ADDRESS = 'me@primal.net';

interface SupportAppDialogProps {
  open: boolean;
  onClose: () => void;
}

// Convert lightning address to LNURL endpoint
async function getLnurlEndpoint(lnAddress: string): Promise<string | null> {
  try {
    const [name, domain] = lnAddress.split('@');
    if (!name || !domain) return null;
    
    const url = `https://${domain}/.well-known/lnurlp/${name}`;
    const response = await fetch(url);
    const data = await response.json();
    
    if (data.callback) {
      return data.callback;
    }
    return null;
  } catch (error) {
    console.error('Failed to get LNURL endpoint:', error);
    return null;
  }
}

export function SupportAppDialog({ open, onClose }: SupportAppDialogProps) {
  const [selectedAmount, setSelectedAmount] = useState<number | null>(1000);
  const [customAmount, setCustomAmount] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  
  const { connections, getActiveConnection, sendPayment } = useNWC();
  const { toast } = useToast();
  
  const hasWallet = connections.some(c => c.isConnected);
  const amount = customAmount ? parseInt(customAmount) : selectedAmount;

  const handleSupport = async () => {
    if (!amount || amount <= 0) return;

    // Check for wallet
    const connection = getActiveConnection();
    if (!connection?.isConnected) {
      setShowWalletModal(true);
      return;
    }

    setIsSending(true);
    try {
      // Get LNURL callback endpoint
      const callback = await getLnurlEndpoint(APP_LIGHTNING_ADDRESS);
      if (!callback) {
        toast({
          title: 'Payment endpoint not found',
          description: 'Could not connect to the payment service.',
          variant: 'destructive',
        });
        setIsSending(false);
        return;
      }

      const amountMsats = amount * 1000; // Convert to millisats

      // Get invoice from LNURL endpoint
      const separator = callback.includes('?') ? '&' : '?';
      const res = await fetch(`${callback}${separator}amount=${amountMsats}`);
      const responseData = await res.json();

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${responseData.reason || 'Unknown error'}`);
      }

      const invoice = responseData.pr;
      if (!invoice || typeof invoice !== 'string') {
        throw new Error('Payment service did not return a valid invoice');
      }

      // Pay using NWC
      await sendPayment(connection, invoice);

      toast({
        title: 'Thank you!',
        description: `You sent ${amount} sats to support Cove!`,
      });
      
      // Show thank you animation
      setShowThankYou(true);
      setTimeout(() => {
        setShowThankYou(false);
        onClose();
      }, 3000);
    } catch (error) {
      console.error('Support payment failed:', error);
      toast({
        title: 'Payment failed',
        description: error instanceof Error ? error.message : 'Something went wrong',
        variant: 'destructive',
      });
    } finally {
      setIsSending(false);
    }
  };

  const formatAmount = (sats: number) => {
    if (sats >= 1000) {
      return `${(sats / 1000).toFixed(sats % 1000 === 0 ? 0 : 1)}k`;
    }
    return sats.toString();
  };

  // Use portal to ensure dialog renders at the top level
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
  }, []);

  // Prevent body scroll when dialog is open
  useEffect(() => {
    if (open) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [open]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  if (!mounted) return null;

  const dialogContent = (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm"
            style={{ zIndex: 99999 }}
          />
          
          {/* Dialog Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto pointer-events-none"
            style={{ zIndex: 100000 }}
          >
            <div 
              className="relative w-full max-w-md my-auto rounded-3xl overflow-hidden bg-gradient-to-b from-zinc-900 to-black border border-white/10 shadow-2xl pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>

              {/* Thank You Animation */}
              <AnimatePresence>
                {showThankYou && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-gradient-to-b from-primary/20 to-pink-500/20 backdrop-blur-sm"
                  >
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: 'spring', damping: 10, stiffness: 100 }}
                    >
                      <div className="h-24 w-24 rounded-full bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center mb-6">
                        <Heart className="h-12 w-12 text-white" fill="white" />
                      </div>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="text-center"
                    >
                      <h3 className="text-2xl font-bold mb-2">Thank You!</h3>
                      <p className="text-muted-foreground">
                        Your support helps keep Cove running!
                      </p>
                    </motion.div>
                    {/* Confetti-like sparkles */}
                    {[...Array(12)].map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ 
                          opacity: 1, 
                          scale: 0,
                          x: 0,
                          y: 0,
                        }}
                        animate={{ 
                          opacity: 0, 
                          scale: 1,
                          x: (Math.random() - 0.5) * 300,
                          y: (Math.random() - 0.5) * 300,
                        }}
                        transition={{ 
                          duration: 1.5,
                          delay: 0.1 + i * 0.05,
                          ease: 'easeOut',
                        }}
                        className="absolute"
                        style={{ 
                          left: '50%', 
                          top: '40%',
                        }}
                      >
                        <Sparkles className={cn(
                          'h-6 w-6',
                          i % 3 === 0 ? 'text-orange-400' : i % 3 === 1 ? 'text-pink-400' : 'text-yellow-400'
                        )} />
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Header */}
              <div className="p-6 pb-4 text-center">
                <motion.div
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  className="inline-flex flex-col items-center gap-3 mb-4"
                >
                  <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary/20 to-pink-500/20 border border-primary/30 flex items-center justify-center">
                    <CoveLogo className="h-10 w-10 text-primary" />
                  </div>
                  <div className="text-center">
                    <h3 className="font-bold text-lg">Support Cove</h3>
                    <p className="text-sm text-muted-foreground">
                      Help keep the music flowing
                    </p>
                  </div>
                </motion.div>
                <p className="text-sm text-muted-foreground">
                  Your support helps maintain servers, develop new features, and keep Cove free for everyone.
                </p>
              </div>

              {/* Amount Selection */}
              <div className="px-6 pb-4">
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {SUPPORT_AMOUNTS.map((amt) => (
                    <motion.button
                      key={amt}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        setSelectedAmount(amt);
                        setCustomAmount('');
                      }}
                      className={cn(
                        'py-3 rounded-xl font-medium text-sm transition-all',
                        selectedAmount === amt && !customAmount
                          ? 'bg-gradient-to-r from-primary to-pink-500 text-white shadow-lg shadow-primary/30'
                          : 'bg-white/5 hover:bg-white/10 border border-white/10'
                      )}
                    >
                      <Zap className="h-3 w-3 inline mr-1" />
                      {formatAmount(amt)}
                    </motion.button>
                  ))}
                </div>

                {/* Custom Amount */}
                <div className="relative">
                  <Zap className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="number"
                    placeholder="Custom amount..."
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      if (e.target.value) setSelectedAmount(null);
                    }}
                    className="pl-9 bg-white/5 border-white/10 rounded-xl"
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="p-6 pt-2">
                <Button
                  onClick={handleSupport}
                  disabled={!amount || amount <= 0 || isSending}
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-primary to-pink-500 hover:from-primary/90 hover:to-pink-500/90 text-white font-bold text-lg"
                >
                  {isSending ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Heart className="h-5 w-5 mr-2" fill="currentColor" />
                      Support with {amount ? amount.toLocaleString() : 0} sats
                    </>
                  )}
                </Button>
                {!hasWallet && (
                  <p className="text-xs text-center text-amber-400 mt-2">
                    Connect a wallet to send support
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  // Portal to body to ensure it's above all other elements
  return (
    <>
      {createPortal(dialogContent, document.body)}
      <WalletModal open={showWalletModal} onOpenChange={setShowWalletModal} />
    </>
  );
}
